<template>
  <div
    class="product-carousel critical-component-clear"
    :class="[
      {
        [$class('hidden')]: isMobile ? section.hideMobile : section.hideDesktop,
        [`section-content--${section.contentColor}`]: isMobile
      },
      ,
    ]"
    :style="`--product-carousel-bg: ${section.backgroundColor};`"
  >
    <div
      class="product-carousel__carousel splide"
      :class="`product-carousel__carousel--${section.layout}`"
      :js-carousel="`product-carousel-${section.id}`"
    >
      <h2
        v-if="section.titleText.length && section.titlePosition === 'top'"
        :class="[
          {
            [$class('hidden')]: isMobile ? section.hideTitleMobile : section.hideTitleDesktop
          },
        ]"
        class="
            product-carousel__title
            product-carousel__title--top
            text-desktop-medium
            text-mobile-medium
          "
        v-text="section.titleText"
      />
      <div class="product-carousel__content">
        <div class="product-carousel__track splide__track">
          <ul class="splide__list">
            <li
              v-for="(slide, index) of slides"
              :key="index"
              class="splide__slide"
              data-gtm-event="product-carousel-slide"
            >
              <product-card
                v-if="slide.type === 'product'"
                :product="slide.data"
                :show-badge="section.showBadges"
                :show-flat-image="section.showFlatImages"
                :show-product-titles="section.showProductTitles"
                :mobile-gallery="false"
                :lazyload="false"
              />
              <product-carousel-media
                v-if="slide.type === 'media'"
                :data="slide.data"
              />
            </li>
          </ul>
        </div>

        <div
          class="splide__arrows product-carousel__controls"
          :class="{
            [$class('active')]: arrowsHovered,
          }"
        >
          <button
            v-show="slides?.length > 3"
            class="
              splide__arrow
              splide__arrow--prev
              product-carousel__prev
            "
            type="button"
            :aria-label="$string('accessibility.carousel.previous')"
            @mouseenter="() => (arrowsHovered = true)"
            @mouseleave="() => (arrowsHovered = false)"
          >
            <icon-arrow-left-thin />
          </button>

          <button
            v-show="slides?.length > 3"
            class="
              splide__arrow
              splide__arrow--next
              product-carousel__next
            "
            type="button"
            :aria-label="$string('accessibility.carousel.next')"
            @mouseenter="() => (arrowsHovered = true)"
            @mouseleave="() => (arrowsHovered = false)"
          >
            <icon-arrow-right-thin />
          </button>
        </div>
      </div>

      <div
        class="product-carousel__footer"
        :class="[
          `section-content--${section.contentColor}`,
        ]"
      >
        <h2
          v-if="section.titleText.length && section.titlePosition === 'bottom'"
          :class="[
            {
              [$class('hidden')]: isMobile ? section.hideTitleMobile : section.hideTitleDesktop
            },
          ]"
          class="
            product-carousel__title
            text-desktop-regular
            text-mobile-regular
          "
          v-text="section.titleText"
        />

        <div
          v-if="section.bodyText.length"
          class="product-carousel__body text-desktop-regular text-mobile-regular"
          v-html="section.bodyText"
        />

        <a
          v-if="section.button.text.length && section.button.url?.length"
          class="product-carousel__button text-desktop-small text-mobile-small text-regular"
          :href="section.button.url"
          v-text="section.button.text"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * Carousel component displaying product cards.
 *
 * @param {Object} section - Section settings.
 * @param {Array} products - Products to be displayed in carousel.
 *
 */
import { mapState } from 'vuex'

import Carousel from '~/api/carousel'
import ProductCard from '~async/product-card/product-card'
import IconArrowRightThin from '~icons/direction/arrow/right-thin.svg'
import IconArrowLeftThin from '~icons/direction/arrow/left-thin.svg'
import ProductCarouselMedia
  from '~async/product-carousel/product-carousel-media.vue'

export default {
  name: 'ProductCarousel',

  components: {
    ProductCarouselMedia,
    ProductCard,
    IconArrowRightThin,
    IconArrowLeftThin,
  },

  props: {
    section: {
      type: Object,
      default: () => ({
        backgroundColor: '',
        carousel: {
          autoplay: false,
          autoplaySpeed: null,
          type: 'loop',
        },
        collection: {},
        contentColor: '',
        button: {
          text: '',
          url: '',
        },
        hideDesktop: false,
        hideMobile: false,
        hideTitleDesktop: false,
        hideTitleMobile: false,
        id: '',
        maxProducts: '',
        titleText: '',
        titleMobileSize: '',
        titleDesktopSize: '',
        showBadges: false,
        showFlatImages: true,
        showProductTitles: false,
      }),
    },
    slides: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      arrowsHovered: false,
      carousel: null,
      carouselIndex: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.createCarousel()
  },

  methods: {

    /**
     * Main carousel contructor function
     */
    createCarousel() {
      const carouselNamespace = `[js-carousel="product-carousel-${this.section.id}"]`

      if (!document.querySelector(carouselNamespace)) {
        return
      }

      const reducedMotion =
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const autoplay = Boolean(
        !reducedMotion && this.section.carousel.autoplay,
      )
      const interval = autoplay ? this.section.carousel.autoplaySpeed : null

      this.carousel = Carousel(carouselNamespace, {
        fixedWidth: '100%',
        type: this.section.carousel.type,
        rewind: true,
        autoplay,
        drag: 'free',
        gap: 'var(--spacing-3)',
        padding: { left: 'var(--spacing-3)', right: 'var(--spacing-3)' },
        interval,
        perMove: 1,
        perPage: 1,
        speed: 1000,
        snap: true,
        pagination: true,
        classes: {
          pagination: 'splide__pagination product-carousel__pagination',
          page: 'splide__pagination__page product-carousel__page',
        },
        breakpoints: {
          1024: {
            arrows: true,
            type: this.section.carousel.type,
            fixedWidth: 'calc(33.33% - 43px)',
            perPage: 3,
            padding: '192px',
            gap: '64px',
            pagination: false,
          },
        },
      })

      /**
       * When a slide updates, save the index to data.
       */
      const events = ['active', 'updated']
      events.forEach(event => {
        this.carousel.on(event, () => {
          this.carouselIndex = this.carousel.index
        })
      })

      this.carousel.mount()
    },
  },
}
</script>

<style lang="scss">
@import "./product-carousel";
</style>
